import { useEffect, useRef, useState } from 'react';
import { Skeleton } from '../../../../ds/components';
import { errorMessage } from '../../../../lib/api';
import { useSessionQuery } from '../../../../lib/session';
import { TwoFactorSetupPanel } from '../../../auth/TwoFactorSetupPanel';
import { OPTIONAL_TWO_FACTOR_TEXT } from '../../user/steps/SecurityStep';
import { WizardButton, WizardIntro } from '../../ui';
import { useWizardNav } from '../wizardContext';
import styles from './steps.module.css';

/**
 * The 2FA at the end of the admin wizard (own heading „Zweiter Faktor", no frame): `TwoFactorSetupPanel` with the
 * recovery codes, then `POST /setup/organization/finish` and Admin 11. An account that already has TOTP finishes
 * at once. While the server does not require 2FA (`twoFactorRequired = false`, D-338) the step is optional:
 * „Später einrichten" finishes without it (the server marks the step skipped).
 */
export function SecurityStep({ compact = false }: { compact?: boolean | undefined }) {
  const nav = useWizardNav();
  const session = useSessionQuery();
  const [error, setError] = useState<string | null>(null);
  const started = useRef(false);
  const hasTotp = session.data?.account.twoFactorEnabled === true;
  const optional = session.data?.twoFactorRequired === false;
  const [skipping, setSkipping] = useState(false);

  async function finish() {
    setError(null);
    try {
      await nav.finish();
    } catch (failure) {
      setError(errorMessage(failure));
      started.current = false;
    }
  }

  async function skip() {
    setSkipping(true);
    await finish();
    setSkipping(false);
  }

  useEffect(() => {
    if (hasTotp && !started.current) {
      started.current = true;
      void finish();
    }
  });

  return (
    <>
      {compact ? null : (
        <WizardIntro
          eyebrow="Zum Schluss"
          title="Zweiter Faktor"
          text={
            optional
              ? OPTIONAL_TWO_FACTOR_TEXT
              : 'Richten Sie die Anmeldung mit einer Authenticator-App ein. Danach ist Ihre Organisation fertig eingerichtet.'
          }
        />
      )}
      {compact && optional && !hasTotp ? (
        <p className={styles.cardAside}>{OPTIONAL_TWO_FACTOR_TEXT}</p>
      ) : null}
      {hasTotp || session.isPending ? (
        <Skeleton variant="block" height={260} />
      ) : (
        <section className={styles.card} aria-label="Zweiter Faktor">
          <TwoFactorSetupPanel
            variant="login"
            onDone={() => void finish()}
            onCancel={() => nav.go('team')}
            cancelLabel="Zurück"
          />
          {optional ? (
            <WizardButton variant="ghost" loading={skipping} onClick={() => void skip()}>
              Später einrichten
            </WizardButton>
          ) : null}
        </section>
      )}
      {error ? (
        <div className={styles.error} role="alert">
          {error}
        </div>
      ) : null}
    </>
  );
}
